import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../lib/auth-context';
import api from '../../lib/api';
import { joinClass } from '../../lib/zoom-join';
import { minutesUntilStart } from '../../lib/session-time';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Crown, Calendar, Clock, Video, BookOpen, Heart, AlertTriangle, ExternalLink, Stethoscope, Salad, Lightbulb, Loader2, Info, Sparkles } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { formatDate, formatTime, formatTimeLocal, formatLocalDate, getLocalDayMonth, getDurationLabel, getStatusColor, getTimezoneAbbr } from '../../lib/utils';
import { toast } from 'sonner';
import TimezoneHeader from '../../components/TimezoneHeader';
import SessionRatingModal from '../../components/SessionRatingModal';
import PregnancyOnboardingModal from '../../components/PregnancyOnboardingModal';
import MilestoneCelebration from '../../components/MilestoneCelebration';
import WelcomeModal from '../../components/WelcomeModal';
import DeliveryCheckDialog from '../../components/DeliveryCheckDialog';
import { BannerCarousel } from '../../components/BannerCarousel';
import { CoachCard } from '../../components/prenatal/CoachCard';
import { TalkToTeamButton } from '../../components/learner/TalkToTeamButton';

export default function LearnerDashboard() {
  const { user, refreshUser } = useAuth();
  const [stats, setStats] = useState({});
  const [upcomingSession, setUpcomingSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelDialog, setCancelDialog] = useState({ open: false });
  const [pendingRatings, setPendingRatings] = useState([]);
  const [ratingSession, setRatingSession] = useState(null);
  const [bufferSettings, setBufferSettings] = useState({ instructor_start_buffer: 10, learner_join_buffer: 5 });
  const [upcomingModal, setUpcomingModal] = useState(false);
  const [myBookings, setMyBookings] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [leadStatus, setLeadStatus] = useState(null);
  const [consultPricing, setConsultPricing] = useState(null);
  const [dailyTip, setDailyTip] = useState(null);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [learnerState, setLearnerState] = useState(null);

  const isElite = user?.learner_type === 'elite';
  const isTrial = user?.learner_type === 'trial_paid';
  const isFree = user?.learner_type === 'free' || !user?.learner_type;
  const isConsult = user?.learner_type === 'gynae_consult';
  const profile = user?.pregnancy_profile || {};
  // V14.1 / V39 — postnatal detection: the server stage (SSOT, includes the EDD + 3 flip) wins;
  // cached profile signals stay as a fallback for a deep link that hasn't hydrated /learner/me yet.
  const isPostnatal = (
    learnerState?.stage === 'postnatal'
    || profile.stage === 'postnatal'
    || profile.course_type === 'postnatal'
    || !!profile.delivery_date
  );
  // V40 (APP V40 §3) — a null week from the API must render NOTHING, never promote the cached
  // pregnancy week or the raw EDD behind it. That fallback chain was the real "Week 41 of 40" bug.
  const weeksPregnant = isPostnatal ? null : (learnerState?.weeks_pregnant ?? profile.week_number);
  const trimester = isPostnatal ? null : (learnerState?.current_trimester ?? profile.trimester);
  const edd = isPostnatal ? null : profile.edd;

  // V13.14.5 — Welcome modal: pop on first login per OPS V24 contract.
  // Backend computes welcome_pending + welcome_kind; we just render.
  const refetchLearnerState = useCallback(() => {
    api.get('/api/learner/me').then((r) => setLearnerState(r.data?.state)).catch(() => {});
  }, []);
  useEffect(() => {
    if (user?.role !== 'learner') return;
    refetchLearnerState();
  }, [user, refetchLearnerState]);

  // Legacy onboarding modal — only fires now if user is missing course_type
  // (e.g. very old accounts pre-V13.14.5 schema). Welcome modal handles EDD.
  useEffect(() => {
    if (user?.role !== 'learner') return;
    if (!user) return;
    const pp = user.pregnancy_profile || {};
    // V13.14.6 — modal writes `stage`, legacy data uses `course_type`.
    // Fall back to either so saving the modal actually dismisses it.
    if (!pp.stage && !pp.course_type) setOnboardingOpen(true);
  }, [user]);

  // Daily tip — fetches once pregnancy_profile is set. API returns the tip
  // matched to current trimester/week.
  useEffect(() => {
    if (!user?.pregnancy_profile?.week_number && !isPostnatal) return;
    api.get('/api/daily-tip/today').then((res) => setDailyTip(res.data)).catch(() => {});
  }, [user?.pregnancy_profile?.week_number, isPostnatal]);

  useEffect(() => {
    Promise.all([
      api.get('/api/dashboard/stats'),
      api.get('/api/learner/upcoming-session'),
      api.get('/api/learner/pending-ratings'),
      api.get('/api/settings/buffers'),
    ]).then(([statsRes, sessionRes, ratingsRes, bufferRes]) => {
      setStats(statsRes.data);
      setUpcomingSession(sessionRes.data);
      setBufferSettings(bufferRes.data);
      const pending = ratingsRes.data.pending || [];
      setPendingRatings(pending);
      if (pending.length > 0) {
        setRatingSession(pending[0]);
      }
    }).catch(() => {}).finally(() => setLoading(false));

    // Fetch lead status and consultation pricing for free/trial users
    if (!isElite) {
      api.get('/api/payment/lead-status').then(res => setLeadStatus(res.data)).catch(() => {});
      api.get('/api/consultation/pricing').then(res => setConsultPricing(res.data)).catch(() => {});
    }
  }, []);

  const handleCancel = async () => {
    if (!upcomingSession) return;
    try {
      const res = await api.post(`/api/sessions/${upcomingSession.id}/unbook`);
      toast.info(res.data.message || 'Dropped out of class');
      setUpcomingSession(null);
      setCancelDialog({ open: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to drop out');
    }
  };

  const daysToEDD = edd ? Math.max(0, Math.ceil((new Date(edd) - new Date()) / (1000 * 60 * 60 * 24))) : null;
  // Postnatal: weeks since delivery — drives the "X weeks postpartum" header
  const deliveryDate = profile.delivery_date;
  const weeksPostpartum = deliveryDate
    ? Math.max(0, Math.floor((Date.now() - new Date(deliveryDate).getTime()) / (1000 * 60 * 60 * 24 * 7)))
    : null;
  const postpartumLabel = weeksPostpartum === null
    ? null
    : weeksPostpartum < 1
      ? 'First week postpartum'
      : `${weeksPostpartum} ${weeksPostpartum === 1 ? 'week' : 'weeks'} postpartum`;

  const handleJoinClass = () => {
    // V14.5 Part 9 — B7 fix: delegate to the mobile-safe joinClass helper.
    // The helper opens a placeholder window SYNCHRONOUSLY inside this
    // click handler (preserving the user-gesture for iOS Safari) and then
    // redirects it once /api/sessions/{id}/link resolves. NEVER move the
    // first `window.open` below an `await` — it WILL be popup-blocked.
    if (!upcomingSession?.id) {
      toast.info('Zoom meeting link will be available when the class is about to start.');
      return;
    }
    return joinClass(upcomingSession.id, upcomingSession.zoom_join_url);
  };

  // Razorpay payment flow — V25: quote-based only (server names the price + currency)
  const initiatePayment = useCallback(async (purpose, placementQuery) => {
    setPaymentLoading(true);
    try {
      // Step 0: server-issued quote (country/currency resolved by the LMS)
      const quoteRes = await api.get(`/api/pricing/catalog?${placementQuery}`);
      const quote = quoteRes.data;
      const itemKey = Object.keys(quote.items || {}).find((k) => quote.items[k]?.price_amount != null);
      const item = itemKey ? quote.items[itemKey] : null;
      if (!quote.quote_id || !item) {
        toast.error('Price unavailable right now. Please retry.');
        setPaymentLoading(false);
        return;
      }
      // Step 1: Create order via OPS from the quote
      const orderRes = await api.post('/api/payment/create-order', {
        quote_id: quote.quote_id, method: 'razorpay', item_key: itemKey,
      });
      const { order_id, ops_payment_id } = orderRes.data;
      const razorpay_key_id = orderRes.data.razorpay_key_id || quote.razorpay_key_id;

      if (!order_id || !razorpay_key_id) {
        toast.error('Unable to create payment order. Please try again.');
        setPaymentLoading(false);
        return;
      }

      // Step 2: Open Razorpay checkout
      const options = {
        key: razorpay_key_id,
        amount: item.razorpay_amount_paise,
        currency: item.razorpay_currency || quote.currency,
        name: 'Soul Sanskar Academy',
        description: purpose === 'trial_class' ? 'Yoga Trial Class' :
                     purpose === 'gynae_consult' ? 'Gynecologist Consultation' :
                     purpose === 'nutritionist_consult' ? 'Nutritionist Consultation' :
                     'Enrollment',
        order_id: order_id,
        handler: async function (response) {
          // Step 3: Verify payment
          try {
            const verifyRes = await api.post('/api/payment/verify', {
              order_id: order_id,
              payment_id: response.razorpay_payment_id,
              signature: response.razorpay_signature,
            });

            if (verifyRes.data.verified) {
              toast.success('Payment successful! Updating your account...');
              // Refresh user data to pick up new learner_type
              if (refreshUser) await refreshUser();
              // Reload to reflect new state
              setTimeout(() => window.location.reload(), 1500);
            } else {
              toast.error('Payment verification failed. Contact support.');
            }
          } catch (err) {
            toast.error('Payment verification error. Contact support if charged.');
          }
          setPaymentLoading(false);
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
          contact: user?.phone || '',
        },
        theme: { color: '#4A6741' },
        modal: {
          ondismiss: function () {
            setPaymentLoading(false);
          },
        },
      };

      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        toast.error('Payment gateway is loading. Please try again.');
        setPaymentLoading(false);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Payment initiation failed';
      toast.error(detail);
      setPaymentLoading(false);
    }
  }, [user, refreshUser]);

  const trialStage = (profile.course_type || profile.stage) === 'postnatal' ? 'postnatal' : 'prenatal';
  const handleBookTrial = () => initiatePayment('trial_class', `placement=trial_class&stage=${trialStage}`);
  const handleBookGynae = () => initiatePayment('gynae_consult', 'placement=consult&consult_type=gynae');
  const handleBookNutritionist = () => initiatePayment('nutritionist_consult', 'placement=consult&consult_type=nutritionist');

  const trialMaxed = leadStatus?.found && (leadStatus?.trial_count || 0) >= 2;

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className={isElite ? 'relative rounded-2xl p-6 sm:p-8 overflow-hidden' : ''}>
        {isElite && <div className="absolute inset-0 bg-gradient-to-r from-amber-50/80 via-amber-100/40 to-transparent rounded-2xl" style={{ boxShadow: '0 18px 60px rgba(245,214,120,0.18)' }} />}
        <div className="relative">
          <div className="flex items-center gap-2 mb-1">
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Namaste, {user?.name?.split(' ')[0]}</h1>
            {isElite && <Badge className="elite-badge" data-testid="elite-member-badge"><Crown className="h-3 w-3 mr-1" />Elite Member</Badge>}
          </div>
          {isPostnatal ? (
            <div className="flex items-center gap-3 text-muted-foreground mt-1 flex-wrap" data-testid="postnatal-status-header">
              <Badge variant="secondary" className="text-xs bg-rose-100 text-rose-800 border-rose-200">Postnatal</Badge>
              {postpartumLabel && <span className="text-sm" data-testid="postpartum-weeks-label">{postpartumLabel}</span>}
            </div>
          ) : trimester ? (
            <div className="flex items-center gap-3 text-muted-foreground mt-1 flex-wrap">
              <span className="text-sm">Trimester {trimester}</span>
              {weeksPregnant && <span className="text-sm">Week {weeksPregnant}</span>}
              {edd && <span className="text-sm">EDD: {formatDate(edd)}</span>}
              {daysToEDD !== null && <Badge variant="secondary" className="text-xs">{daysToEDD} days to go</Badge>}
            </div>
          ) : null}
        </div>
      </div>

      {/* V16.3 — suspended-account banner: login is allowed, everything else is blocked */}
      {(user?.is_suspended || learnerState?.is_suspended) && (
        <Card className="rounded-2xl border-rose-300 bg-rose-50" data-testid="dashboard-suspended-banner">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-rose-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-rose-900">Your account is currently paused</p>
                <p className="text-sm text-rose-800 mt-0.5">
                  Access to live classes, recorded courses and bookings is blocked.
                  To restore access, please contact the admin team.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* V33 — Elite membership window ended (OPS course_expiry_date): content listed, playback/booking blocked */}
      {!learnerState?.is_suspended && learnerState?.membership?.access_blocked && (
        <Card className="rounded-2xl border-amber-300 bg-amber-50" data-testid="dashboard-membership-expired-banner">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-amber-900">Your Elite membership ended on {learnerState.membership.course_expiry_date}</p>
                <p className="text-sm text-amber-800 mt-0.5">
                  Live classes and recorded courses are paused — nothing is lost. Renew with our team to pick up right where you left off.
                </p>
                <TalkToTeamButton plan={learnerState.membership?.blocked?.cta?.plan || 'elite_renewal'}
                  label={learnerState.membership?.blocked?.cta?.label || 'Talk to our team about renewing'}
                  testId="dashboard-membership-renew-btn"
                  className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* V36.1 — prenatal window (EDD + 7) ended: account active, prenatal content revoked, postnatal continues */}
      {!learnerState?.is_suspended && learnerState?.membership?.prenatal_window?.status === 'ended' && (
        <Card className="rounded-2xl border-teal-200 bg-teal-50" data-testid="dashboard-prenatal-window-ended-banner">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-teal-700 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-teal-900">Your prenatal programme is complete</p>
                <p className="text-sm text-teal-800 mt-0.5" data-testid="dashboard-prenatal-window-message">
                  {learnerState.membership.prenatal_window.message}
                </p>
                <TalkToTeamButton plan={learnerState.membership.prenatal_window.cta?.plan || 'postnatal_programme'}
                  label={learnerState.membership.prenatal_window.cta?.label || 'Talk to our team about postnatal'}
                  testId="dashboard-prenatal-window-contact-btn"
                  className="border-teal-300 bg-white text-teal-900 hover:bg-teal-100" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* V14.6 — Promotional banners (web parity with the App home banners) */}
      <BannerCarousel placement="home_top" />

      {/* V13.14.7 — suppress milestones + welcome until mandatory postnatal
          onboarding is done, so MTTF-migrated learners aren't double-modaled.
          V14.5 Part 19u — also suppress until the prenatal/postnatal gateway
          (PregnancyOnboardingModal) is answered, AND skip WelcomeModal
          entirely for postnatal learners (founder: "If the learner chooses
          postnatal, then don't show the trial/explore modal"). */}
      {!user?.pending_postnatal_onboarding && !onboardingOpen && <MilestoneCelebration />}

      {!user?.pending_postnatal_onboarding && !onboardingOpen && !isPostnatal && (
        <WelcomeModal
          state={learnerState}
          onAcknowledged={(s) => setLearnerState(s)}
        />
      )}

      {/* V17 — "Has your baby arrived?" from EDD day onward (prenatal accounts only) */}
      {!user?.pending_postnatal_onboarding && !onboardingOpen && !isPostnatal && !learnerState?.welcome_pending && (
        <DeliveryCheckDialog prompt={learnerState?.delivery_prompt} onUpdated={refetchLearnerState} />
      )}

      {isTrial && !upcomingSession && (
        <Card className="rounded-2xl border-blue-200 bg-blue-50">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-900">Your trial class is being scheduled</p>
                <p className="text-xs text-blue-700">
                  {isPostnatal
                    ? 'Our team will contact you and book the trial as per your delivery date and recovery profile.'
                    : 'Our team will contact you and book the trial as per your pregnancy trimester and risk profile.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Personalized daily tip — matched to current week/trimester */}
      {dailyTip && (
        <Card className="rounded-2xl border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="daily-tip-card">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <Lightbulb className="h-5 w-5 text-amber-700" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <p className="font-heading text-base font-semibold">{dailyTip.title || "Today's tip for you"}</p>
                  {dailyTip.category && <Badge variant="outline" className="text-[10px] capitalize">{dailyTip.category}</Badge>}
                  {!isPostnatal && dailyTip.trimester && <Badge variant="outline" className="text-[10px]">T{dailyTip.trimester}</Badge>}
                </div>
                <p className="text-sm text-foreground/80 leading-relaxed">{dailyTip.content || dailyTip.tip || dailyTip.description}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* SoulAI Prenatal Coach — hidden for paid-trial learners until they enrol
          (founder, 3 Oct 2026: it confused trial users on the web portal) */}
      {!isPostnatal && !isTrial && <CoachCard />}

      {/* No-show warning */}
      {stats.no_show_count > 0 && (
        <Card className="rounded-2xl border-amber-200 bg-amber-50">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="text-sm font-medium text-amber-900">No-show warning: {stats.no_show_count}/2</p>
                <p className="text-xs text-amber-700">
                  {stats.no_show_count >= 2 
                    ? `You're blocked from booking until ${stats.no_show_blocked_until ? new Date(stats.no_show_blocked_until).toLocaleDateString('en', { day: 'numeric', month: 'short' }) : 'next session'}.`
                    : 'Dropping out within 2 hours of class or not attending counts as a no-show. 2 no-shows will block your next booking.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Free user — Book Trial CTA */}
      {isFree && !upcomingSession && (stats.my_batches || 0) === 0 && !trialMaxed && (
        <Card className="rounded-2xl border-emerald-200 bg-emerald-50" data-testid="book-trial-card">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Heart className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="text-sm font-medium text-emerald-900">
                    {isPostnatal ? 'Book your first postnatal yoga class' : 'Book your first yoga class'}
                  </p>
                  <p className="text-xs text-emerald-700">
                    {isPostnatal
                      ? 'Start your postnatal recovery journey with a trial class'
                      : 'Start your pregnancy yoga journey with a trial class'}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="book-first-class-button"
                onClick={handleBookTrial}
                disabled={paymentLoading}
              >
                {paymentLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Book a trial class
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trial maxed — contact team */}
      {isFree && trialMaxed && (
        <Card className="rounded-2xl border-amber-200 bg-amber-50" data-testid="trial-maxed-card">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="text-sm font-medium text-amber-900">You've used your 2 trial classes</p>
                <p className="text-xs text-amber-700">Contact our team for ELITE enrollment to access all classes and features.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Consultation Section — V13.14.6: Coming Soon for everyone, payment disabled.
          Future (backlog): SoulAI gathers context (1-2 follow-up questions), then routes
          to the actual consultant who sees the context first. See ROADMAP.md. */}
      {(isFree || isTrial || isConsult || isElite) && consultPricing && (
        <Card className="rounded-2xl border-violet-200 bg-violet-50/50" data-testid="consultation-section">
          <CardContent className="pt-5 pb-4">
            <p className="text-sm font-semibold text-violet-900 mb-3">Expert Consultations</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {consultPricing.gynae && (
                <div className="flex items-center justify-between p-3 rounded-xl bg-white border border-violet-100 opacity-70">
                  <div className="flex items-center gap-2">
                    <Stethoscope className="h-4 w-4 text-violet-400" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Chat with Gynecologist</p>
                      <p className="text-[11px] text-violet-700 font-medium">Coming soon</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" disabled className="border-violet-200 text-violet-400 cursor-not-allowed" data-testid="book-gynae-button">
                    Coming soon
                  </Button>
                </div>
              )}
              {consultPricing.nutritionist && (
                <div className="flex items-center justify-between p-3 rounded-xl bg-white border border-violet-100 opacity-70">
                  <div className="flex items-center gap-2">
                    <Salad className="h-4 w-4 text-green-400" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Chat with Nutritionist</p>
                      <p className="text-[11px] text-green-700 font-medium">Coming soon</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" disabled className="border-green-200 text-green-400 cursor-not-allowed" data-testid="book-nutritionist-button">
                    Coming soon
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {upcomingSession && (() => {
        // IST-aware time math — correct in any browser timezone
        const minutesUntil = minutesUntilStart(upcomingSession);
        // V14.3 — minutesUntilEnd lets us show "ends in X min" when the
        // class is actively running, so the learner has confidence the
        // class is still joinable.
        const minutesUntilEnd = (() => {
          if (minutesUntil === null) return null;
          return minutesUntil + (upcomingSession.duration_minutes || 60);
        })();
        const isLiveByTime = minutesUntil !== null && minutesUntil <= 0 && minutesUntilEnd > 0;
        const isLive = upcomingSession.status === 'live' || isLiveByTime;
        const canJoin = isLive || (minutesUntil !== null && minutesUntil <= (bufferSettings.learner_join_buffer || 5));

        const getTimeUntilLabel = () => {
          if (minutesUntil === null) return '';
          if (isLive) {
            // Class is currently running — show how long until it ends.
            const minsLeft = Math.max(0, Math.ceil(minutesUntilEnd));
            return `Live now · ends in ${minsLeft} min`;
          }
          if (minutesUntil < 60) return `Starts in ${Math.ceil(minutesUntil)} min`;
          const hours = Math.floor(minutesUntil / 60);
          if (hours < 24) return `Starts in ${hours}h ${Math.ceil(minutesUntil % 60)}m`;
          const days = Math.floor(hours / 24);
          return `Starts in ${days} day${days > 1 ? 's' : ''}`;
        };

        return (
        <Card className="rounded-2xl border-primary/20 bg-primary/5" data-testid="upcoming-session-card">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">
                    {isLive ? 'Live Class' : 'Upcoming Class'}
                  </p>
                  {isLive && (
                    <Badge className="bg-rose-600 text-white border-0 gap-1 animate-pulse" data-testid="live-now-badge">
                      <span className="h-1.5 w-1.5 rounded-full bg-white" />Live
                    </Badge>
                  )}
                  <TimezoneHeader />
                </div>
                <h3 className="text-lg font-semibold">{upcomingSession.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{upcomingSession.batch_name} with {upcomingSession.instructor_name}</p>
                <div className="flex items-center gap-4 mt-2 flex-wrap">
                  <span className="flex items-center gap-1 text-sm"><Calendar className="h-4 w-4" />{formatLocalDate(upcomingSession.scheduled_date, upcomingSession.scheduled_time)}</span>
                  <span className="flex items-center gap-1 text-sm"><Clock className="h-4 w-4" />{formatTimeLocal(upcomingSession.scheduled_time, upcomingSession.scheduled_date)}</span>
                  <Badge variant="secondary">{getDurationLabel(upcomingSession.duration_minutes)}</Badge>
                  <Badge variant={isLive ? 'default' : 'secondary'} className={isLive ? 'bg-rose-600 text-white border-0' : 'text-xs'}>
                    {getTimeUntilLabel()}
                  </Badge>
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {canJoin ? (
                  <Button onClick={handleJoinClass} data-testid="join-class-button">
                    <Video className="h-4 w-4 mr-2" />{isLive ? 'Join Live Class' : 'Join Class'}
                    {upcomingSession.zoom_join_url && <ExternalLink className="h-3 w-3 ml-1" />}
                  </Button>
                ) : (
                  <Button disabled className="opacity-40" data-testid="join-class-button-disabled">
                    <Clock className="h-4 w-4 mr-2" />{getTimeUntilLabel()}
                  </Button>
                )}
                {/* V65 — only Elite members manage their own bookings; a trial class is
                    rescheduled or cancelled by the team. */}
                {['elite', 'gold', 'full'].includes(user?.learner_type) && <Button variant="ghost" size="sm" className="text-destructive text-xs" onClick={() => setCancelDialog({ open: true })} data-testid="cancel-class-button">Drop Out</Button>}
              </div>
            </div>
          </CardContent>
        </Card>
        );
      })()}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="rounded-2xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => window.location.href = `/${user?.role}/batches`}>
          <CardContent className="pt-5 pb-4">
            <BookOpen className="h-5 w-5 text-primary mb-3" />
            <p className="text-2xl font-semibold">{stats.my_batches || 0}</p>
            <p className="text-xs text-muted-foreground">My Batches</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl cursor-pointer hover:shadow-md transition-shadow" onClick={async () => {
          try {
            const res = await api.get('/api/sessions/my-bookings');
            setMyBookings(res.data.bookings || []);
            setUpcomingModal(true);
          } catch { setUpcomingModal(true); }
        }}>
          <CardContent className="pt-5 pb-4">
            <Calendar className="h-5 w-5 text-blue-600 mb-3" />
            <p className="text-2xl font-semibold">{stats.upcoming_sessions || 0}</p>
            <p className="text-xs text-muted-foreground">Upcoming</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => window.location.href = `/${user?.role}/batches`}>
          <CardContent className="pt-5 pb-4">
            <Clock className="h-5 w-5 text-emerald-600 mb-3" />
            <p className="text-2xl font-semibold">{stats.attendance_pct ?? 0}%</p>
            <div className="flex items-center gap-1">
              <p className="text-xs text-muted-foreground">Attendance</p>
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="What does Attendance mean?"
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                      className="text-muted-foreground/70 hover:text-emerald-600 transition-colors"
                      data-testid="attendance-info-btn"
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[240px] text-left leading-relaxed" data-testid="attendance-info-tooltip">
                    Attendance % = classes you attended ÷ classes you booked, across all your batches. Only past (conducted) classes count — classes you didn&apos;t book aren&apos;t included.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </CardContent>
        </Card>
        {stats.has_1on1_batch && (
          <Card className="rounded-2xl"><CardContent className="pt-5 pb-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Credits</p>
            <p className="text-2xl font-semibold mt-1">{stats.credits_remaining || 0}<span className="text-sm text-muted-foreground">/{stats.credits_total || 0}</span></p>
          </CardContent></Card>
        )}
      </div>

      <Dialog open={cancelDialog.open} onOpenChange={(o) => setCancelDialog({ open: o })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Drop Out of Class</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Are you sure you want to drop out of this class? Your spot will be given to the next person on the waitlist.</p>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
              <p className="text-xs text-amber-800">Dropping out within 2 hours of class start will count as a no-show. 2 no-shows in a month will block your next booking.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog({ open: false })}>Keep Booking</Button>
            <Button variant="destructive" onClick={handleCancel}>Drop Out</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session Rating Modal */}
      <SessionRatingModal
        session={ratingSession}
        open={!!ratingSession}
        onClose={() => {
          // V14.5 Part 19z20 — persist the skip so it doesn't re-prompt on next login.
          const skippedId = ratingSession?.session_id || ratingSession?.id;
          if (skippedId) {
            api.post('/api/learner/skip-rating', { session_id: skippedId }).catch(() => {});
          }
          const remaining = pendingRatings.filter(p => (p.session_id || p.id) !== skippedId);
          setPendingRatings(remaining);
          setRatingSession(remaining.length > 0 ? remaining[0] : null);
        }}
        onSubmitted={() => {
          const remaining = pendingRatings.filter(p => (p.session_id || p.id) !== (ratingSession?.session_id || ratingSession?.id));
          setPendingRatings(remaining);
          setRatingSession(remaining.length > 0 ? remaining[0] : null);
        }}
      />

      {/* Upcoming Bookings Modal */}
      <Dialog open={upcomingModal} onOpenChange={setUpcomingModal}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">My Upcoming Classes</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="upcoming-bookings-list">
            {myBookings.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No upcoming bookings</p>
                <Button size="sm" className="mt-3" onClick={() => { setUpcomingModal(false); window.location.href = `/${user?.role}/book`; }}>Book a Class</Button>
              </div>
            ) : (
              myBookings.map(s => {
                const minsUntil = minutesUntilStart(s);
                const canJoinThis = s.status === 'live' || (minsUntil !== null && minsUntil <= (bufferSettings.learner_join_buffer || 5));
                const timeLabel = minsUntil !== null ? (
                  minsUntil < 0 ? 'Started' :
                  minsUntil < 60 ? `${Math.ceil(minsUntil)} min` :
                  minsUntil < 1440 ? `${Math.floor(minsUntil / 60)}h` :
                  `${Math.floor(minsUntil / 1440)}d`
                ) : '';
                return (
                  <div key={s.id} className="p-3 rounded-xl border hover:bg-muted/20 transition-colors" data-testid={`upcoming-booking-${s.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="text-center min-w-[40px] bg-primary/10 rounded-lg px-2 py-1">
                          {(() => {
                            const dm = getLocalDayMonth(s.scheduled_date, s.scheduled_time);
                            return (<>
                              <p className="text-[9px] text-primary uppercase">{dm.month}</p>
                              <p className="text-base font-semibold text-primary leading-tight">{dm.day}</p>
                            </>);
                          })()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{s.batch_name || s.title}</p>
                          <p className="text-xs text-muted-foreground">{s.instructor_name && `with ${s.instructor_name} · `}{formatTimeLocal(s.scheduled_time, s.scheduled_date)} · {s.duration_minutes}m</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {canJoinThis ? (
                          <Button size="sm" onClick={() => joinClass(s.id, s.zoom_join_url)} data-testid={`join-booking-${s.id}`}>
                            <Video className="h-3 w-3 mr-1" />Join
                          </Button>
                        ) : (
                          <Badge variant="secondary" className="text-xs">{timeLabel}</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            {myBookings.length > 0 && myBookings.length < 3 && (
              <p className="text-[10px] text-muted-foreground text-center pt-2">You can book up to {3 - myBookings.length} more class{3 - myBookings.length !== 1 ? 'es' : ''}</p>
            )}
            {myBookings.length >= 3 && (
              <p className="text-[10px] text-amber-600 text-center pt-2">Maximum 3 upcoming bookings reached</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* First-login onboarding — forces learners to set pregnancy profile */}
      <PregnancyOnboardingModal
        open={onboardingOpen && !user?.needs_phone_verification}
        onClose={() => setOnboardingOpen(false)}
        forceMode
      />
    </div>
  );
}
